import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { DEFAULT_SHARE_MAX_RESPONSES } from '@/contracts/share'
import {
  clampPaneWidth,
  formSettingsPaneDefaults,
  getTwoPaneGridTemplate,
} from '@/lib/resizable-panes'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import { useFormBuilderStore } from '@/stores/formBuilderStore'
import type { Form, FormStatus } from '@/types/form'
import {
  CheckCircle2,
  ExternalLink,
  FileText,
  ImageIcon,
  PartyPopper,
  Pencil,
  ShieldCheck,
  Sparkles,
  Upload,
  X,
} from 'lucide-react'
import { type CSSProperties, type PointerEvent, type ReactNode, useState } from 'react'
import {
  type EditableImageValue,
  ImageEditorDialog,
  readImageFileAsDataUrl,
} from './ImageEditorDialog'

type WelcomeScreen = Form['welcomeScreen']
type CompletionScreen = Form['thankYouScreen']
type AnimationStyle = NonNullable<CompletionScreen['animationStyle']>
type SettingValue = string | number | boolean | null

const animationOptions: Array<{
  value: AnimationStyle
  label: string
  description: string
  icon: typeof CheckCircle2
}> = [
  {
    value: 'check_confetti',
    label: '勾勾與撒花',
    description: '沿用目前完成動畫，適合多數問卷。',
    icon: PartyPopper,
  },
  {
    value: 'spotlight',
    label: '焦點進場',
    description: '標題與 CTA 依序淡入，適合正式品牌頁。',
    icon: Sparkles,
  },
  {
    value: 'card_stack',
    label: '回饋卡片',
    description: '以卡片層次呈現下一步動作。',
    icon: CheckCircle2,
  },
  {
    value: 'minimal',
    label: '極簡靜態',
    description: '低動態、快速完成，適合內部表單。',
    icon: CheckCircle2,
  },
]

export function FormSettingsDialog({ trigger }: { trigger?: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [welcomeImageEditorOpen, setWelcomeImageEditorOpen] = useState(false)
  const [navWidth, setNavWidth] = useState(230)
  const [navCollapsed, setNavCollapsed] = useState(false)
  const form = useFormBuilderStore((state) => state.form)
  const updateForm = useFormBuilderStore((state) => state.updateForm)
  const welcomeScreen = form.welcomeScreen
  const completionScreen = form.thankYouScreen
  const featureVisibility = useSystemFeatureVisibility()
  const canShowStartPageSettings = featureVisibility['forms.startPage'] === true

  function updateSettings(patch: Record<string, SettingValue>) {
    updateForm({ settings: { ...form.settings, ...patch } })
  }

  function updateWelcomeScreen(patch: Partial<WelcomeScreen>) {
    updateForm({ welcomeScreen: { ...welcomeScreen, ...patch } })
  }

  function updateCompletionScreen(patch: Partial<CompletionScreen>) {
    updateForm({ thankYouScreen: { ...completionScreen, ...patch } })
  }

  async function updateWelcomeImageAsset(file: File | undefined) {
    if (!file) return
    const url = await readImageFileAsDataUrl(file)
    updateWelcomeScreen({
      coverUrl: url,
      coverAlt: welcomeScreen.coverAlt || `${welcomeScreen.title || form.title} 開始頁圖片`,
      coverFit: welcomeScreen.coverFit ?? 'cover',
      coverFocalX: welcomeScreen.coverFocalX ?? 50,
      coverFocalY: welcomeScreen.coverFocalY ?? 50,
      coverZoom: welcomeScreen.coverZoom ?? 1,
      coverRadius: welcomeScreen.coverRadius ?? 16,
      coverHeight: welcomeScreen.coverHeight ?? 300,
    })
    setWelcomeImageEditorOpen(true)
  }

  const welcomeImageValue = toWelcomeImageValue(welcomeScreen)
  const settingsGridColumns = getTwoPaneGridTemplate({
    navWidth,
    navCollapsed,
    contentMinWidth: 0,
  })

  function startNavResize(event: PointerEvent<HTMLButtonElement>) {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = navWidth

    function onPointerMove(moveEvent: globalThis.PointerEvent) {
      setNavCollapsed(false)
      setNavWidth(
        clampPaneWidth(startWidth + moveEvent.clientX - startX, formSettingsPaneDefaults.nav),
      )
    }

    function onPointerUp() {
      window.removeEventListener('pointermove', onPointerMove)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp, { once: true })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {trigger ?? (
            <Button className="w-full" variant="outline">
              <FileText className="size-4" />
              問卷設定
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="left-[10px] top-[10px] h-[calc(100dvh-20px)] max-h-none w-[calc(100dvw-20px)] max-w-none translate-x-0 translate-y-0 overflow-hidden p-0">
          <div className="flex h-full min-h-0 flex-col bg-slate-50">
            <div className="shrink-0 border-b border-slate-200 bg-white p-5 pr-14">
              <DialogHeader>
                <DialogTitle>問卷設定</DialogTitle>
                <DialogDescription className="sr-only">問卷設定</DialogDescription>
              </DialogHeader>
            </div>

            <Tabs
              defaultValue="basic"
              orientation="vertical"
              data-testid="form-settings-tabs"
              className="grid min-h-0 flex-1 grid-cols-1 transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--form-settings-grid-columns)]"
              style={{ '--form-settings-grid-columns': settingsGridColumns } as CSSProperties}
            >
              <div className="relative min-h-0 min-w-0 overflow-hidden">
                <aside
                  data-testid="form-settings-nav"
                  className="min-h-0 border-b border-slate-200 bg-white p-3 lg:h-full lg:border-r lg:border-b-0"
                >
                  <TabsList className="flex h-auto w-full flex-row justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-0 lg:flex-col lg:overflow-visible">
                    <TabsTrigger className="w-full justify-start px-3 py-2" value="basic">
                      基本
                    </TabsTrigger>
                    {canShowStartPageSettings ? (
                      <TabsTrigger className="w-full justify-start px-3 py-2" value="welcome">
                        開始頁
                      </TabsTrigger>
                    ) : null}
                    <TabsTrigger className="w-full justify-start px-3 py-2" value="completion">
                      完成頁
                    </TabsTrigger>
                    <TabsTrigger className="w-full justify-start px-3 py-2" value="status">
                      狀態
                    </TabsTrigger>
                    <TabsTrigger className="w-full justify-start px-3 py-2" value="security">
                      安全
                    </TabsTrigger>
                    <TabsTrigger className="w-full justify-start px-3 py-2" value="b2b">
                      B2B 流程
                    </TabsTrigger>
                  </TabsList>
                </aside>
                <PaneResizeHandle
                  edge="right"
                  collapsed={navCollapsed}
                  resizeLabel="拖曳調整問卷設定左欄寬度"
                  collapseLabel="收合問卷設定左欄"
                  expandLabel="展開問卷設定左欄"
                  onResizeStart={startNavResize}
                  onToggle={() => setNavCollapsed((collapsed) => !collapsed)}
                />
              </div>

              <ScrollArea className="min-h-0 flex-1" viewportClassName="p-5">
                <TabsContent value="basic" className="mt-0">
                  <PanelSection
                    title="基本資訊"
                    description="這些內容會出現在後台管理、分享與填答頁 meta 資訊。"
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field label="問卷標題" htmlFor="form-title">
                        <Input
                          id="form-title"
                          value={form.title}
                          onChange={(event) => updateForm({ title: event.target.value })}
                          placeholder="例如：新客戶申請表"
                        />
                      </Field>
                      <Field label="表單識別用途" htmlFor="form-purpose">
                        <Select
                          value={String(form.settings.formPurpose ?? 'customer_onboarding')}
                          onValueChange={(formPurpose) => updateSettings({ formPurpose })}
                        >
                          <SelectTrigger id="form-purpose">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">一般問卷</SelectItem>
                            <SelectItem value="customer_onboarding">B2B客戶建檔</SelectItem>
                            <SelectItem value="event_registration">活動報名</SelectItem>
                            <SelectItem value="internal_survey">內部調查</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <Field label="問卷描述" htmlFor="form-description">
                      <Textarea
                        id="form-description"
                        value={form.description}
                        onChange={(event) => updateForm({ description: event.target.value })}
                        placeholder="說明問卷目的、資料用途與填寫對象。"
                      />
                    </Field>
                  </PanelSection>
                </TabsContent>

                {canShowStartPageSettings ? (
                  <TabsContent value="welcome" className="mt-0">
                    <PanelSection
                      title="開始頁"
                      description="可選擇是否顯示開始頁；未啟用時，填答者會直接看到第一題。"
                    >
                      <ToggleRow
                        title="啟用開始頁"
                        description="開啟後填答者會先看到開始頁；關閉時直接進入第一題。"
                        checked={welcomeScreen.enabled === true}
                        onChange={(enabled) => updateWelcomeScreen({ enabled })}
                      />
                      <div className="grid gap-4 md:grid-cols-2">
                        <Field label="開始頁標題" htmlFor="welcome-title">
                          <Input
                            id="welcome-title"
                            value={welcomeScreen.title}
                            onChange={(event) => updateWelcomeScreen({ title: event.target.value })}
                            placeholder="歡迎填寫這份問卷"
                          />
                        </Field>
                        <Field label="圖片替代文字" htmlFor="welcome-alt">
                          <Input
                            id="welcome-alt"
                            value={welcomeScreen.coverAlt ?? ''}
                            onChange={(event) =>
                              updateWelcomeScreen({ coverAlt: event.target.value })
                            }
                            placeholder="描述開始頁圖片內容"
                          />
                        </Field>
                      </div>
                      <Field label="開始頁描述" htmlFor="welcome-description">
                        <Textarea
                          id="welcome-description"
                          value={welcomeScreen.description}
                          onChange={(event) =>
                            updateWelcomeScreen({ description: event.target.value })
                          }
                          placeholder="說明填寫目的、預估時間或資料使用方式。"
                        />
                      </Field>
                      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
                        <div className="space-y-3">
                          <label
                            htmlFor="welcome-cover-upload"
                            className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 p-4 text-sm transition hover:border-indigo-300 hover:bg-indigo-50/40"
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <Upload className="size-4 text-slate-500" />
                              <span className="truncate text-slate-700">
                                {welcomeScreen.coverUrl ? '重新上傳開始頁圖片' : '上傳開始頁圖片'}
                              </span>
                            </span>
                            <span className="text-xs text-slate-500">選擇後立即編輯</span>
                            <Input
                              id="welcome-cover-upload"
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              onChange={(event) =>
                                void updateWelcomeImageAsset(event.target.files?.[0])
                              }
                            />
                          </label>
                          <div className="relative">
                            <ImageIcon className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400" />
                            <Input
                              value={welcomeScreen.coverUrl ?? ''}
                              className="pr-10 pl-9"
                              placeholder="https://example.com/welcome.jpg"
                              onChange={(event) =>
                                updateWelcomeScreen({
                                  coverUrl: event.target.value,
                                  coverAlt:
                                    welcomeScreen.coverAlt ||
                                    `${welcomeScreen.title || form.title} 開始頁圖片`,
                                })
                              }
                              onBlur={() => {
                                if (welcomeScreen.coverUrl) setWelcomeImageEditorOpen(true)
                              }}
                            />
                            {welcomeScreen.coverUrl && (
                              <button
                                type="button"
                                className="-translate-y-1/2 absolute top-1/2 right-2 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                                aria-label="清除開始頁圖片"
                                onClick={() => updateWelcomeScreen({ coverUrl: '' })}
                              >
                                <X className="size-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-3">
                          <ImageMiniPreview
                            value={welcomeImageValue}
                            emptyLabel="尚未設定開始頁圖片"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            disabled={!welcomeScreen.coverUrl}
                            onClick={() => setWelcomeImageEditorOpen(true)}
                          >
                            <Pencil className="size-4" />
                            編輯圖片
                          </Button>
                        </div>
                      </div>
                    </PanelSection>
                  </TabsContent>
                ) : null}

                <TabsContent value="completion" className="mt-0">
                  <CompletionSettingsPanel
                    form={form}
                    completionScreen={completionScreen}
                    updateSettings={updateSettings}
                    updateCompletionScreen={updateCompletionScreen}
                  />
                </TabsContent>

                <TabsContent value="status" className="mt-0">
                  <PanelSection title="狀態" description="控制問卷是否可填寫、回應上限與截止時間。">
                    <div className="grid gap-4 md:grid-cols-3">
                      <Field label="問卷狀態" htmlFor="form-status">
                        <Select
                          value={form.status}
                          onValueChange={(status) => updateForm({ status: status as FormStatus })}
                        >
                          <SelectTrigger id="form-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">草稿</SelectItem>
                            <SelectItem value="published">上線</SelectItem>
                            <SelectItem value="closed">已關閉</SelectItem>
                            <SelectItem value="archived">已封存</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="最大回應數" htmlFor="max-responses">
                        <Input
                          id="max-responses"
                          type="number"
                          min={0}
                          value={Number(form.settings.maxResponses ?? DEFAULT_SHARE_MAX_RESPONSES)}
                          onChange={(event) =>
                            updateSettings({
                              maxResponses:
                                Number(event.target.value) || DEFAULT_SHARE_MAX_RESPONSES,
                            })
                          }
                        />
                      </Field>
                      <Field label="截止時間" htmlFor="closed-at">
                        <Input
                          id="closed-at"
                          type="datetime-local"
                          value={String(form.settings.closedAt ?? '')}
                          onChange={(event) => updateSettings({ closedAt: event.target.value })}
                        />
                      </Field>
                    </div>
                    <ToggleRow
                      title="顯示進度條"
                      description="填答頁依目前外觀設定顯示進度。"
                      checked={form.settings.showProgress !== false}
                      onChange={(showProgress) => updateSettings({ showProgress })}
                    />
                  </PanelSection>
                </TabsContent>

                <TabsContent value="security" className="mt-0">
                  <PanelSection
                    title="安全"
                    description="先建立前端 UI contract，後端與資料庫規則會在後續批次實作。"
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <ToggleRow
                        title="需要登入才能填寫"
                        description="正式填寫預設需要登入；preview / demo 測試模式可免登入驗收。"
                        checked={form.settings.requireRespondentLogin !== false}
                        onChange={(requireRespondentLogin) =>
                          updateSettings({ requireRespondentLogin })
                        }
                      />
                    </div>
                    <Field label="填寫密碼" htmlFor="form-password">
                      <Input
                        id="form-password"
                        type="password"
                        value={String(form.settings.responsePassword ?? '')}
                        onChange={(event) =>
                          updateSettings({ responsePassword: event.target.value })
                        }
                        placeholder="先作為 UI contract，後續接後端驗證。"
                      />
                    </Field>
                  </PanelSection>
                </TabsContent>

                <TabsContent value="b2b" className="mt-0">
                  <PanelSection
                    title="B2B 流程"
                    description="設定業務填寫、主管審核與客戶建檔狀態的前端流程欄位。"
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field label="流程模式" htmlFor="customer-workflow">
                        <Select
                          value={String(form.settings.customerWorkflow ?? 'standard')}
                          onValueChange={(customerWorkflow) => updateSettings({ customerWorkflow })}
                        >
                          <SelectTrigger id="customer-workflow">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">一般問卷</SelectItem>
                            <SelectItem value="customer_onboarding">新客戶申請</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="預設建檔狀態" htmlFor="customer-status">
                        <Select
                          value={String(form.settings.defaultCustomerStatus ?? 'not_filed')}
                          onValueChange={(defaultCustomerStatus) =>
                            updateSettings({ defaultCustomerStatus })
                          }
                        >
                          <SelectTrigger id="customer-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_filed">ERP未建檔</SelectItem>
                            <SelectItem value="filed">ERP已建檔</SelectItem>
                            <SelectItem value="archived">已歸檔</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <ToggleRow
                        title="需要主管審核"
                        description="業務提交後進入主管審核清單。"
                        checked={form.settings.requireManagerReview !== false}
                        onChange={(requireManagerReview) =>
                          updateSettings({ requireManagerReview })
                        }
                      />
                      <ToggleRow
                        title="審核後自動建立客戶資料"
                        description="先建立前端流程旗標，後續批次再接資料模型。"
                        checked={Boolean(form.settings.autoCreateCustomer)}
                        onChange={(autoCreateCustomer) => updateSettings({ autoCreateCustomer })}
                      />
                    </div>
                  </PanelSection>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      <ImageEditorDialog
        open={welcomeImageEditorOpen}
        onOpenChange={setWelcomeImageEditorOpen}
        title="編輯開始頁圖片"
        description="中型彈窗內調整圖片裁切、焦點、縮放與圓角。這裡只使用本機預覽，不接 R2。"
        value={welcomeImageValue}
        defaultAlt={`${welcomeScreen.title || form.title} 開始頁圖片`}
        uploadId="welcome-image-editor-upload"
        showHeightControl
        heightRange={{ min: 160, max: 420, step: 10 }}
        onChange={(value) =>
          updateWelcomeScreen({
            coverUrl: value.url,
            coverAlt: value.alt,
            coverFit: value.fit,
            coverFocalX: value.focalX,
            coverFocalY: value.focalY,
            coverZoom: value.zoom,
            coverRadius: value.radius,
            coverHeight: value.height,
          })
        }
        onRemove={() =>
          updateWelcomeScreen({
            coverUrl: '',
            coverAlt: '',
            coverFit: 'cover',
            coverFocalX: 50,
            coverFocalY: 50,
            coverZoom: 1,
            coverRadius: 16,
            coverHeight: 300,
          })
        }
      />
    </>
  )
}

function CompletionSettingsPanel({
  form,
  completionScreen,
  updateSettings,
  updateCompletionScreen,
}: {
  form: Form
  completionScreen: CompletionScreen
  updateSettings: (patch: Record<string, SettingValue>) => void
  updateCompletionScreen: (patch: Partial<CompletionScreen>) => void
}) {
  const animationStyle = completionScreen.animationStyle ?? 'check_confetti'

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-5">
        <PanelSection
          title="完成頁文字"
          description="完成頁就是填答完成後的感謝與 CTA 頁，所有送出後內容都集中在這裡。"
        >
          <Field label="完成頁標題" htmlFor="completion-title">
            <Input
              id="completion-title"
              value={completionScreen.title}
              onChange={(event) => updateCompletionScreen({ title: event.target.value })}
              placeholder="已收到您的回覆"
            />
          </Field>
          <Field label="完成頁描述" htmlFor="completion-description">
            <Textarea
              id="completion-description"
              value={completionScreen.description}
              onChange={(event) => updateCompletionScreen({ description: event.target.value })}
              placeholder="謝謝您完成這份問卷。"
            />
          </Field>
        </PanelSection>

        <PanelSection
          title="CTA 與下一步"
          description="預設只顯示「再次填寫新問卷」，其他 CTA 與分享按鈕需明確開啟。"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="主要 CTA 文字" htmlFor="primary-cta-label">
              <Input
                id="primary-cta-label"
                value={completionScreen.primaryCtaLabel ?? ''}
                onChange={(event) =>
                  updateCompletionScreen({ primaryCtaLabel: event.target.value })
                }
                placeholder="查看網站"
              />
            </Field>
            <Field label="主要 CTA 連結" htmlFor="primary-cta-url">
              <Input
                id="primary-cta-url"
                value={completionScreen.primaryCtaUrl ?? ''}
                onChange={(event) => updateCompletionScreen({ primaryCtaUrl: event.target.value })}
                placeholder="https://example.com"
              />
            </Field>
            <Field label="次要 CTA 文字" htmlFor="secondary-cta-label">
              <Input
                id="secondary-cta-label"
                value={completionScreen.secondaryCtaLabel ?? ''}
                onChange={(event) =>
                  updateCompletionScreen({ secondaryCtaLabel: event.target.value })
                }
                placeholder="下載資料"
              />
            </Field>
            <Field label="次要 CTA 連結" htmlFor="secondary-cta-url">
              <Input
                id="secondary-cta-url"
                value={completionScreen.secondaryCtaUrl ?? ''}
                onChange={(event) =>
                  updateCompletionScreen({ secondaryCtaUrl: event.target.value })
                }
                placeholder="https://example.com/download"
              />
            </Field>
          </div>
          <Field label="再次填寫新問卷按鈕文字" htmlFor="new-submission-label">
            <Input
              id="new-submission-label"
              value={completionScreen.newSubmissionButtonLabel ?? ''}
              onChange={(event) =>
                updateCompletionScreen({ newSubmissionButtonLabel: event.target.value })
              }
              placeholder="再次填寫新問卷"
            />
          </Field>
          <div className="grid gap-3 md:grid-cols-2">
            <ToggleRow
              title="顯示再次填寫新問卷"
              description="預設顯示，讓填答者送出後可開始一份新的空白問卷。"
              checked={completionScreen.showNewSubmissionButton !== false}
              onChange={(showNewSubmissionButton) =>
                updateCompletionScreen({ showNewSubmissionButton })
              }
            />
            <ToggleRow
              title="顯示主要 CTA"
              description="開啟後才會顯示主要 CTA，例如回到官網。"
              checked={completionScreen.showPrimaryCta === true}
              onChange={(showPrimaryCta) => updateCompletionScreen({ showPrimaryCta })}
            />
            <ToggleRow
              title="顯示次要 CTA"
              description="開啟後才會顯示次要 CTA，例如查看更多範例。"
              checked={completionScreen.showSecondaryCta === true}
              onChange={(showSecondaryCta) => updateCompletionScreen({ showSecondaryCta })}
            />
            <ToggleRow
              title="顯示重新填寫"
              description="舊版重新填寫按鈕，預設關閉。"
              checked={completionScreen.showResubmitButton === true}
              onChange={(showResubmitButton) => {
                updateCompletionScreen({ showResubmitButton })
                updateSettings({ allowResubmit: showResubmitButton })
              }}
            />
            <ToggleRow
              title="顯示分享按鈕"
              description="提供複製連結、Email 等分享動作，預設關閉。"
              checked={completionScreen.showShareButtons === true}
              onChange={(showShareButtons) => updateCompletionScreen({ showShareButtons })}
            />
          </div>
        </PanelSection>

        <PanelSection
          title="導頁與動畫"
          description="保留完成頁動畫效果、導頁 URL 與延遲導頁設定。"
        >
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_160px]">
            <Field label="導頁網址" htmlFor="redirect-url">
              <Input
                id="redirect-url"
                value={completionScreen.redirectUrl ?? ''}
                onChange={(event) => updateCompletionScreen({ redirectUrl: event.target.value })}
                placeholder="https://example.com/next"
              />
            </Field>
            <Field label="等待秒數" htmlFor="redirect-delay">
              <Input
                id="redirect-delay"
                type="number"
                min={0}
                max={30}
                value={completionScreen.redirectDelaySeconds ?? 5}
                onChange={(event) =>
                  updateCompletionScreen({
                    redirectDelaySeconds: Number(event.target.value) || 0,
                  })
                }
              />
            </Field>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {animationOptions.map((option) => {
              const Icon = option.icon
              const selected = animationStyle === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    'flex min-h-24 items-start gap-4 rounded-lg border border-slate-200 p-4 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50',
                    selected && 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100',
                  )}
                  onClick={() => updateCompletionScreen({ animationStyle: option.value })}
                >
                  <span className="grid size-10 shrink-0 place-items-center rounded-md bg-indigo-100 text-indigo-700">
                    <Icon className="size-5" />
                  </span>
                  <span>
                    <span className="block font-semibold text-slate-950">{option.label}</span>
                    <span className="mt-1 block text-sm leading-5 text-slate-500">
                      {option.description}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </PanelSection>
      </div>

      <aside className="rounded-lg border border-slate-200 bg-white p-5 xl:sticky xl:top-5 xl:self-start">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-950">完成頁預覽</h3>
          <p className="mt-1 text-sm text-slate-500">依目前設定顯示大致排版。</p>
        </div>
        <div
          className={cn(
            'overflow-hidden rounded-lg border border-slate-200 p-5 text-center',
            animationStyle === 'spotlight' && 'bg-indigo-950 text-white',
            animationStyle === 'card_stack' && 'bg-slate-100',
            animationStyle !== 'spotlight' && 'bg-white',
          )}
        >
          <div className="mx-auto grid size-14 place-items-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 className="size-7" />
          </div>
          <div className="mt-4 text-2xl font-semibold">{completionScreen.title}</div>
          <p
            className={cn(
              'mt-3 text-sm leading-6',
              animationStyle === 'spotlight' ? 'text-indigo-100' : 'text-slate-600',
            )}
          >
            {completionScreen.description}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {completionScreen.showNewSubmissionButton !== false && (
              <Button size="sm">
                {completionScreen.newSubmissionButtonLabel || '再次填寫新問卷'}
              </Button>
            )}
            {completionScreen.showPrimaryCta === true && completionScreen.primaryCtaLabel && (
              <Button size="sm">
                {completionScreen.primaryCtaLabel}
                {completionScreen.primaryCtaUrl && <ExternalLink className="size-3.5" />}
              </Button>
            )}
            {completionScreen.showSecondaryCta === true && completionScreen.secondaryCtaLabel && (
              <Button size="sm" variant="outline">
                {completionScreen.secondaryCtaLabel}
              </Button>
            )}
            {Boolean(form.settings.allowResubmit) &&
              completionScreen.showResubmitButton === true && (
                <Button size="sm" variant="secondary">
                  重新填寫
                </Button>
              )}
          </div>
          {completionScreen.redirectUrl && (
            <div
              className={cn(
                'mt-4 rounded-md px-3 py-2 text-xs',
                animationStyle === 'spotlight'
                  ? 'bg-white/10 text-indigo-100'
                  : 'bg-slate-100 text-slate-500',
              )}
            >
              {completionScreen.redirectDelaySeconds ?? 5} 秒後導向：
              {completionScreen.redirectUrl}
            </div>
          )}
        </div>
      </aside>
    </div>
  )
}

function PanelSection({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
      <div>
        <h3 className="font-semibold text-slate-950">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
      </div>
      {children}
    </section>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: ReactNode
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}

function ToggleRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string
  description: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-4">
      <span>
        <span className="flex items-center gap-2 text-sm font-medium text-slate-950">
          <ShieldCheck className="size-4 text-indigo-600" />
          {title}
        </span>
        <span className="mt-1 block text-xs leading-5 text-slate-500">{description}</span>
      </span>
      <Switch aria-label={title} checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

function ImageMiniPreview({
  value,
  emptyLabel,
}: {
  value: EditableImageValue
  emptyLabel: string
}) {
  return (
    <div
      className="relative h-32 w-full overflow-hidden border border-slate-200 bg-slate-100 shadow-sm"
      style={{ borderRadius: value.radius }}
    >
      {value.url ? (
        <img
          src={value.url}
          alt={value.alt}
          className="size-full"
          style={{
            objectFit: value.fit,
            objectPosition: `${value.focalX}% ${value.focalY}%`,
            transform: `scale(${value.zoom})`,
            transformOrigin: `${value.focalX}% ${value.focalY}%`,
          }}
        />
      ) : (
        <div className="grid size-full place-items-center text-center text-sm text-slate-500">
          <span>
            <ImageIcon className="mx-auto mb-2 size-5 text-slate-400" />
            {emptyLabel}
          </span>
        </div>
      )}
    </div>
  )
}

function toWelcomeImageValue(welcomeScreen: WelcomeScreen): EditableImageValue {
  return {
    url: welcomeScreen.coverUrl ?? '',
    alt: welcomeScreen.coverAlt ?? '',
    fit: welcomeScreen.coverFit ?? 'cover',
    focalX: welcomeScreen.coverFocalX ?? 50,
    focalY: welcomeScreen.coverFocalY ?? 50,
    zoom: welcomeScreen.coverZoom ?? 1,
    radius: welcomeScreen.coverRadius ?? 16,
    height: welcomeScreen.coverHeight ?? 300,
  }
}
